<template>
  <div
    class="ui-select-container"
    :class="classList"
    ref="select"
    @click="$emit('click')"
  >
    <div class="ui-select-container__header" ref="header">
      <ui-input-container
        class="ui-select-container__hood"
        :title="title"
        :has-value="hasValue"
        :has-title="hasTitle"
        :focus="isActiveSearch"
        :error="error"
        :small="small"
        :type="type"
        @click="toggleFocus"
      >
        <template #after>
          <div class="ui-select-container__icon-list">
            <ui-loading
              class="ui-select-container__icon-list--icon"
              v-if="valueLoading"
              size="small"
              hideTransition
            />
            <ui-icon
              class="ui-select-container__icon-list--icon hover-icon clear-icon"
              v-if="showClearIcon"
              name="roundClose"
              @click.stop="clearValue"
            />
            <ui-icon
              class="ui-select-container__icon-list--icon arrow"
              v-if="showArrowIcon"
              :class="{active: isFocus}"
              name="bottom"
            />
            <slot name="after" />
          </div>
        </template>
        <template #before>
          <slot name="before" />
        </template>
        <slot name="before-value" />
        <slot name="value">
          <template v-if="!multiple">
            <div
              v-if="isActiveSearch"
              class="ui-select-container__search"
            >
              <input
                class="ui-select-container__search--input"
                ref="search"
                :value="innerSearchValue"
                @input="onInputSearchValue"
                @click.stop
              >
            </div>
            <div
              class="ui-select-container__value"
              v-if="!innerSearchValue"
            >
              <slot
                v-if="hasValue"
                name="selectedOption"
                :item="activeOptionsList[0]"
              >
                {{onceValueTitle}}
              </slot>
              <template v-else>
                {{innerPlaceholder}}
              </template>
            </div>
          </template>
          <div v-else class="ui-select-container__badge-list">
            <ui-select-badge
              class="ui-select-container__badge-list--item"
              v-for="activeItem in activeOptions"
              :key="activeItem[idFieldName]"
              :title="activeItem[titleFieldName]"
              :text-color="activeItem.textColor || badgeTextColor"
              :background-color="activeItem.backgroundColor || badgeBackgroundColor"
              :disabled="disabled"
              @click.native.stop
              @close="removeOption(activeItem)"
            >
              <slot name="selectedOption" :item="activeItem" />
            </ui-select-badge>
            <div
              class="ui-select-container__search"
              v-if="isActiveSearch"
            >
              <input
                class="ui-select-container__search--input multiple"
                :value="innerSearchValue"
                :disabled="disabled"
                :class="{'has-value': hasValue}"
                :placeholder="innerPlaceholder"
                ref="search"
                @input="onInputSearchValue"
                @click.stop
              >
            </div>
          </div>
        </slot>
      </ui-input-container>
      <ui-dropdown
        v-if="isFocus"
        ref="option"
        :style="optionStyle"
        :container="$refs.header"
        :max-height="maxHeight"
        v-click-outside="outsideClickEvent"
      >
        <template v-if="!loading">
          <slot name="beforeOption"/>
          <ui-select-option-item
            v-if="isEmpty"
            :value="emptyText"
          />
          <template v-else>
            <slot />
          </template>
          <slot name="afterOption"/>
        </template>
        <ui-select-option-item
          v-if="hasLoading"
          class="ui-select-container__option--loading"
        >
          <ui-loading
            size="small"
            hideTransition
          />
        </ui-select-option-item>
        <div
          v-if="intersectionScroll"
          class="ui-select-container__intersection-trigger"
          v-intersection="scrollEndHandler"
        />
      </ui-dropdown>
      <div
        v-if="hasDescription"
        class="ui-select-container__description"
        tabindex="-1"
      >
        <slot name="description" />
      </div>
    </div>
  </div>
</template>

<script>
import UiInputContainer from '../input/UiInputContainer.vue'
import UiSelectBadge from './UiSelectBadge.vue'
import UiLoading from '../loader/UiLoading.vue'
import UiIcon from '../icons/UiIcon.vue'
import UiSelectOptionItem from './UiSelectOptionItem.vue'
import UiDropdown from '../UiDropdown.vue'

import selectContainerMixin from '../../mixins/selectContainerMixin'
import inputFocusMixin from '../../mixins/inputFocusMixin'
import intersection from '../../directives/intersection'

import { debounce, getDropdownPosition, isEmpty } from '../../libs/helpTools'
import { POSITIONS } from '../../libs/helpConsts'

export default {
  mixins: [inputFocusMixin, { props: selectContainerMixin.props }],
  directives: { intersection },
  components: { UiInputContainer, UiSelectBadge, UiLoading, UiIcon, UiSelectOptionItem, UiDropdown },
  props: {
    value: [String, Number, Array, Boolean, Symbol],
    activeOptions: [Object, Array],
    titleFieldName: {
      type: String,
      default: 'name'
    },
    badgeTextColor: {
      type: String,
      default: ''
    },
    badgeBackgroundColor: {
      type: String,
      default: ''
    },
    isClear: {
      type: Boolean,
      default: false
    },
    clearableValue: {
      default: null
    },
    isEmpty: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    valueLoading: {
      type: Boolean,
      default: false
    },
    optionsLoading: {
      type: Boolean,
      default: false
    },
    hasOptionBorder: {
      type: Boolean,
      default: true
    },
    maxWidth: {
      type: Number,
      default: 350
    }
  },
  data () {
    return {
      bodyOptionStyle: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: 0
      },
      optionPosition: {
        x: POSITIONS.RIGHT,
        y: POSITIONS.BOTTOM
      },
      optionWidth: null,
      initLoading: false
    }
  },
  computed: {
    innerValue: {
      get () {
        if (this.multiple) {
          return this.value ?? []
        }
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    isEqual: selectContainerMixin.computed.isEqual,
    activeOptionsList () {
      if (isEmpty(this.activeOptions)) return []
      return Array.isArray(this.activeOptions)
        ? this.activeOptions
        : [this.activeOptions]
    },
    onceValueTitle () {
      if (this.multiple) return
      const activeOption = this.activeOptionsList[0]
      if (isEmpty(activeOption)) return null
      return activeOption?.[this.titleFieldName]
    },
    innerPlaceholder () {
      return this.placeholder ?? (this.small ? this.title : 'Поиск')
    },
    hasValue () {
      return Boolean(this.activeOptionsList.length)
    },
    showClearIcon () {
      return this.hasValue && this.isClear && !this.valueLoading && !this.disabled
    },
    isActiveSearch () {
      return this.search && this.isFocus
    },
    optionStyle () {
      if (this.appendToBody) return this.bodyOptionStyle
      return null
    },
    innerSearchValue: {
      get () {
        return this.searchValue
      },
      set (value) {
        this.$emit('update:search-value', value)
      }
    },
    outsideClickEvent () {
      return {
        handler: this.blur,
        events: ['click'],
        isActive: this.isFocus
      }
    },
    debounceCalcBodyOptionStyle () {
      return debounce(this.calcBodyOptionStyle, 100)
    },
    debounceSetOptionPosition () {
      return debounce(this.setOptionPosition, 100)
    },
    debounceOptionPositionMethod () {
      return this.appendToBody
        ? this.debounceCalcBodyOptionStyle
        : this.debounceSetOptionPosition
    },
    classList () {
      return {
        'size-s': this.small,
        disabled: this.disabled,
        'option-border': this.hasOptionBorder,
        _search: this.isActiveSearch
      }
    },
    hasLoading () {
      return this.valueLoading || this.loading || this.optionsLoading
    }
  },
  watch: {
    loading (isLoading) {
      if (isLoading) return

      if (this.initLoading) {
        this.optionWidth = null
        this.setOptionWidth()
        this.endInitLoading()
        return
      }

      if (this.isFocus && !this.optionWidth) {
        this.setOptionWidth()
      }
    }
  },
  beforeDestroy () {
    if (this.isFocus) {
      this.revertBodyOptionStyle()
    }
  },
  methods: {
    onInputSearchValue (e) {
      this.innerSearchValue = e.target.value
    },
    setOptionPosition () {
      this.optionPosition = getDropdownPosition(this.$refs.select, this.maxHeight, this.maxWidth)
    },
    clearValue () {
      if (this.clearableValue !== null) {
        this.innerValue = this.clearableValue
      } else {
        this.innerValue = this.multiple ? [] : null
      }
      this.focusSearch()
      this.$emit('clear')
    },
    removeOption (option) {
      this.innerValue = this.innerValue.filter(value => !this.isEqual(value, option[this.idFieldName]))
    },
    focusSearch () {
      if (!this.search) return
      this.$nextTick(() => this.$refs.search?.focus())
    },
    focus () {
      if (this.disabled) return

      if (!this.appendToBody) this.setOptionPosition()
      this.$emit('open-dropdown')
      this.setFocus(true)
      setTimeout(() => this.loading && this.startInitLoading(), 0)

      if (this.search) {
        this.innerSearchValue = ''
        this.focusSearch()
      }
      if (this.appendToBody) this.$nextTick(this.appendOption)

      window.addEventListener('resize', this.debounceOptionPositionMethod)
      if (!this.loading) this.setOptionWidth()
    },
    startInitLoading () {
      this.initLoading = true
    },
    endInitLoading () {
      this.initLoading = false
    },
    setOptionWidth () {
      this.$nextTick(() => this.optionWidth = this.$refs.optionContainer?.offsetWidth)
    },
    clearOptionWidth () {
      this.optionWidth = null
    },
    blur () {
      this.$emit('close-dropdown')
      this.setFocus(false)
      this.innerSearchValue = ''
      this.revertBodyOptionStyle()
      this.clearOptionWidth()
      this.endInitLoading()
      window.removeEventListener('resize', this.debounceOptionPositionMethod)
    },
    revertBodyOptionStyle () {
      if (!this.$refs.option?.$el) return
      this.$refs.header.append(this.$refs.option?.$el)
    },
    appendOption () {
      this.calcBodyOptionStyle()
      const body = document.body
      body.append(this.$refs.option?.$el)
    },
    calcBodyOptionStyle () {
      if (!this.isFocus) return

      const { bottom, left, width } = this.$refs.select.getBoundingClientRect()

      const topPosition = bottom + window.pageYOffset
      const leftPosition = left + window.pageXOffset

      this.bodyOptionStyle.top = `${topPosition}px`
      this.bodyOptionStyle.left = `${leftPosition}px`
      this.bodyOptionStyle.width = `${width}px`
    },
    scrollEndHandler () {
      this.$emit('scroll-end')
    }
  }
}
</script>

<style lang="scss" scoped>
$badge-margin-top: 4px;
$badge-height: 24px;
$title-margin-bottom: 2px;

.ui-select-container {
  $parent: &;
  position: relative;

  &.disabled {
    cursor: initial;
    pointer-events: none;
    background: $gray-7;

    ::v-deep() {
      .ui-input-container {
        background: $gray-7;
        fill: $gray-3;
      }

      #{$parent}__value {
        color: $gray-3;
      }
    }
  }

  &._search {
    #{$parent}__value {
      position: absolute;
      color: $gray-06;
    }
  }

  &__value {
    @include ellipsis;
    width: 100%;
  }

  &__header {
    position: relative;
  }

  &__hood {
    cursor: pointer;
  }

  &__badge-list {
    @include flex-wrap-list(10px, 4px);
    flex-grow: 1;
    overflow: hidden;

    &--item {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }

  &__values {
    display: flex;
    flex-grow: 1;
    flex-wrap: wrap;
    margin-right: -10px;
    margin-top: -$badge-margin-top;
    overflow: hidden;

    &--item {
      @include ellipsis;

      margin-top: $badge-margin-top;
      margin-right: 10px;
    }
  }

  &__option-container {
    position: absolute;
    min-width: 100%;
    width: max-content;
    z-index: 2;

    @include media-max-sm {
      //Перекрываем инлайновое вычисление ширины
      width: 100% !important;
    }

    &._position {
      &_top {
        top: unset;
        bottom: 100%;

        #{$parent}__option {
          margin-top: unset;
          margin-bottom: 1px;
          box-shadow: 0 -12px 16px rgba($gray-3, 0.2);
        }
      }

      &_left {
        right: 0;
      }
    }
  }

  &__option {
    @include pretty-scrollbar-s;
    position: relative;
    display: flex;
    flex-direction: column;
    margin-top: 1px;
    border: 1px solid $gray-6;
    border-radius: $border-radius-01;
    box-shadow: 0 12px 16px rgba($gray-3, 0.2);
    overflow-y: auto;
    z-index: 1;

    &--empty {
      color: $black-02 !important
    }

    &--loading {
      @include flex-center;
    }
  }

  &__icon-list {
    display: flex;
    align-items: center;

    &--icon {
      margin-left: 10px;

      &.hover-icon {
        fill: $gray-05;
        transition: fill .1s;
        cursor: pointer;

        &:hover {
          fill: $gray-02
        }
      }

      &.arrow {
        transform: rotate(0);
        transition: transform .2s;

        &.active {
          transform: rotate(180deg);
        }
      }
    }
  }

  &__search {
    display: flex;
    flex: 1;
    z-index: 1;

    &--input {
      @include ellipsis;
      display: flex;
      flex-grow: 1;
      height: 20px;
      width: 20px;
      padding: 0;
      background-color: inherit;
      border: initial;

      &.multiple {
        width: 100px;

        &.has-value {
          height: $badge-height;
        }
      }

      &::placeholder {
        color: $gray-06 !important;
      }
    }
  }

  &__intersection-trigger {
    position: relative;
    bottom: 25px;
    height: 0;
    width: 100%;
    background-color: transparent;
    border: none !important;
    pointer-events: none;
    user-select: none;
  }

  &__description {
    @include font-14;
    margin-top: 5px;
    line-height: 20px;
    color: $gray-3;
  }

  &.option-border {
    ::v-deep(.ui-select-container__option) {
      @include nextChildren {
        border-top: 1px solid $gray-6;
      }
    }
  }

  &.size-s {
    .ui-select-container__option--loading {
      padding: 8px 16px;
    }
  }
}
</style>
